import React from 'react';
import { motion } from 'framer-motion';
import { Cookie } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEOMeta from '../components/SEOMeta';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="font-serif text-xl font-semibold text-[var(--color-deep)] mb-3 pb-2 border-b border-[var(--color-border)]">{title}</h2>
    <div className="space-y-3 text-sm text-[var(--color-muted-text)] leading-relaxed">{children}</div>
  </div>
);

const CookieRow = ({ name, type, purpose, duration }) => (
  <tr className="border-b border-[var(--color-border)] last:border-0">
    <td className="py-2 pr-4 font-mono text-xs text-[var(--color-ink)]">{name}</td>
    <td className="py-2 pr-4 text-xs">{type}</td>
    <td className="py-2 pr-4 text-xs">{purpose}</td>
    <td className="py-2 text-xs">{duration}</td>
  </tr>
);

export default function CookiePolicyPage() {
  const handleReset = () => {
    localStorage.removeItem('zampa_cookie_consent');
    window.location.reload();
  };

  return (
    <div className="bg-[var(--color-cream)] min-h-screen">
      <SEOMeta
        title="Cookie Policy"
        description="Cookie Policy di Una Zampa In Paradiso S.r.l.s. — come utilizziamo i cookie e come gestire le tue preferenze."
        url="/cookie-policy"
      />

      {/* Hero */}
      <section className="bg-[var(--color-deep)] py-14">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-2 text-[var(--color-sage)] text-xs font-bold uppercase tracking-widest mb-3">
              <Cookie size={13} /> Documento Legale
            </div>
            <h1 className="font-serif text-4xl font-semibold text-white mb-2">Cookie Policy</h1>
            <p className="text-white/60 text-sm">Ai sensi dell'art. 122 del D.Lgs. 196/2003 e del Provvedimento Garante del 10 giugno 2021</p>
            <p className="text-white/40 text-xs mt-2">Ultimo aggiornamento: giugno 2025</p>
          </motion.div>
        </div>
      </section>

      <section className="section-py">
        <div className="container-custom max-w-3xl">
          <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-8 shadow-sm">

            <Section title="1. Cosa Sono i Cookie">
              <p>I cookie sono piccoli file di testo memorizzati nel browser dell'utente quando visita un sito web. Vengono utilizzati per ricordare preferenze, sessioni di accesso e per raccogliere dati statistici anonimi sul comportamento di navigazione.</p>
            </Section>

            <Section title="2. Tipologie di Cookie Utilizzati">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b-2 border-[var(--color-border)]">
                      <th className="pb-2 pr-4 text-xs font-semibold text-[var(--color-ink)] uppercase tracking-wide">Nome</th>
                      <th className="pb-2 pr-4 text-xs font-semibold text-[var(--color-ink)] uppercase tracking-wide">Tipo</th>
                      <th className="pb-2 pr-4 text-xs font-semibold text-[var(--color-ink)] uppercase tracking-wide">Finalità</th>
                      <th className="pb-2 text-xs font-semibold text-[var(--color-ink)] uppercase tracking-wide">Durata</th>
                    </tr>
                  </thead>
                  <tbody>
                    <CookieRow name="zampa_auth_token" type="Tecnico — Sessione" purpose="Mantiene attiva la sessione di accesso dell'utente" duration="7 giorni" />
                    <CookieRow name="zampa_cookie_consent" type="Tecnico — Preferenze" purpose="Memorizza la scelta del consenso ai cookie" duration="12 mesi" />
                    <CookieRow name="i18n_lang" type="Tecnico — Preferenze" purpose="Ricorda la lingua selezionata (IT/EN)" duration="12 mesi" />
                    <CookieRow name="_ga, _gid" type="Analitico — Terze parti (Google Analytics)" purpose="Statistiche anonime di traffico (solo se accettati)" duration="2 anni / 24h" />
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-xs italic">I cookie tecnici non richiedono consenso in quanto necessari al funzionamento del portale.</p>
            </Section>

            <Section title="3. Cookie di Terze Parti">
              <p>Il portale non utilizza cookie di profilazione di terze parti. In caso di utilizzo futuro di Google Analytics, gli utenti saranno informati e potranno rifiutare. Google Analytics è configurato con IP anonimizzato e senza condivisione dei dati.</p>
            </Section>

            <Section title="4. Come Gestire i Cookie">
              <p>Puoi gestire o disabilitare i cookie direttamente dal tuo browser:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[var(--color-sage)] hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/it/kb/Gestione%20dei%20cookie" target="_blank" rel="noopener noreferrer" className="text-[var(--color-sage)] hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[var(--color-sage)] hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/it-it/microsoft-edge/eliminare-i-cookie-in-microsoft-edge" target="_blank" rel="noopener noreferrer" className="text-[var(--color-sage)] hover:underline">Microsoft Edge</a></li>
              </ul>
              <p className="mt-3">La disabilitazione dei cookie tecnici potrebbe compromettere il corretto funzionamento del portale.</p>
            </Section>

            <Section title="5. Revoca del Consenso">
              <p>Puoi revocare in qualsiasi momento il consenso ai cookie non tecnici cliccando il pulsante qui sotto. Il banner verrà nuovamente visualizzato al prossimo accesso.</p>
              <div className="mt-4">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="border-[var(--color-terracotta)] text-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-soft)] rounded-xl"
                  data-testid="reset-cookie-consent"
                >
                  Reimposta preferenze cookie
                </Button>
              </div>
            </Section>

            <Section title="6. Contatti">
              <p>Per qualsiasi informazione relativa ai cookie: <a href="mailto:privacy@unazampainparadiso.com" className="text-[var(--color-sage)] hover:underline">privacy@unazampainparadiso.com</a></p>
            </Section>

          </div>
        </div>
      </section>
    </div>
  );
}
